import { Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { Button, TextInputField } from 'utoniq-ui'

import {
  Maybe,
  MembershipInterval,
  UpdateSubscriptionInput,
} from '@/clients/utoniq-core/schema'
import {
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'
import { MembershipSubscriptionProduct } from '@/core/membership'

export type MembershipEditFormProps = {
  product?: Maybe<MembershipSubscriptionProduct>
  onSubmit: (values: EditMembershipProps) => Promise<void>
}

export type EditMembershipProps = UpdateSubscriptionInput

const schema: UseFormProps['schema'] = o => ({
  title: o
    .string()
    .max(50)
    .allow(null)
    .optional()
    .label(tLabel('consoleMembershipEdit.title')),
  content: o.string().max(2000).label(tLabel('consoleMembershipEdit.content')),
  annualContent: o
    .string()
    .allow(null, '')
    .optional()
    .max(2000)
    .label(tLabel('consoleMembershipEdit.annualContent')),
})

export const MembershipEditForm: Organism<MembershipEditFormProps> = ({
  onSubmit,
  product,
}) => {
  const { t } = useTranslation('consoleMembershipEdit')
  const { showError } = useMessage()

  const {
    handleSubmit,
    register,
    setError,
    reset,
    formState: { isSubmitting, errors, isValid },
  } = useForm<EditMembershipProps>({
    schema,
    defaultValues,
  })

  useEffect(() => {
    if (product) {
      reset({
        title: product.title,
        content: product.content,
        annualContent: product.annualContent,
      })
    }
  }, [product])

  const handleOnSubmit = handleSubmit(values =>
    onSubmit(values).catch(error => {
      handleError<EditMembershipProps>(error, setError, showError)
    })
  )

  return (
    <OrganismFrame sx={{ px: 5 }}>
      <TextInputField
        type="input"
        t={t}
        textFieldProps={{ sx: { mb: 0 } }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        {...register('title')}
        error={errors?.title}
      />
      <Stack pb={5}>
        <Typography variant="h5">
          {product?.plans
            .map(plan => t(`${plan.interval ?? MembershipInterval.Month}lyFee`))
            .join('/')}
        </Typography>
        <Typography variant="subtitle1">
          {product?.plans
            .map(plan => `${plan.formattedAmount}${t('yen')}`)
            .join('/')}
        </Typography>
      </Stack>
      <TextInputField
        type="textarea"
        t={t}
        textFieldProps={{
          multiline: true,
          rows: 13,
          sx: { mb: 0 },
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        titleProps={{ variant: 'h5', color: 'text.primary' }}
        {...register('content')}
        error={errors?.content}
      />
      {product?.plans && product?.plans?.length > 1 && (
        <TextInputField
          type="textarea"
          t={t}
          textFieldProps={{
            multiline: true,
            rows: 13,
            sx: { mb: 0 },
            InputProps: { sx: { px: 4, py: 3 } },
          }}
          titleProps={{ variant: 'h5', color: 'text.primary' }}
          {...register('annualContent')}
          error={errors?.annualContent}
        />
      )}

      <Typography variant="subtitle1" sx={{ mt: 7 }}>
        {t('aboutSales')}
      </Typography>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="overline">・</Typography>
        <Typography variant="caption">{t('aboutSalesDetail1')}</Typography>
      </Stack>
      <Stack direction="row" sx={{ mt: 2 }}>
        <Typography variant="overline">・</Typography>
        <Typography variant="caption">{t('aboutSalesDetail2')}</Typography>
      </Stack>
      <Stack sx={{ mt: 6, mb: 4 }} justifyContent="center" alignItems="center">
        <Button
          sx={{ width: 244, py: 3 }}
          onClick={handleOnSubmit}
          loading={isSubmitting}
          disabled={!isValid || isSubmitting}
        >
          {t('change')}
        </Button>
      </Stack>
    </OrganismFrame>
  )
}

const defaultValues: EditMembershipProps = {
  content: '',
  title: '',
  annualContent: null,
}
